import React from 'react';
import Drawer from '@mui/material/Drawer/Drawer';
import Box from '@mui/material/Box/Box';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import Chip from '@mui/material/Chip/Chip';
import LinearProgress from '@mui/material/LinearProgress/LinearProgress';
import List from '@mui/material/List/List';
import ListItem from '@mui/material/ListItem/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon/ListItemIcon';
import ListItemText from '@mui/material/ListItemText/ListItemText';
import Checkbox from '@mui/material/Checkbox/Checkbox';
import Button from '@mui/material/Button/Button';
import Divider from '@mui/material/Divider/Divider';
import { QuestGroupView, QuestGroupId } from '../../hooks/useProfileQuests';

interface ProfileQuestDrawerProps {
    open: boolean;
    onClose: () => void;
    questGroups: QuestGroupView[];
    totals: {
        totalCount: number;
        completedCount: number;
        remainingCount: number;
    };
    setGroupSkippedOrCompleted: (groupId: QuestGroupId, value: boolean) => void;
}

export const ProfileQuestDrawer = ({
    open,
    onClose,
    questGroups,
    totals,
    setGroupSkippedOrCompleted
}: ProfileQuestDrawerProps) => {
    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ width: 360, p: 2 }}>
                <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">Quests</Typography>
                        <Chip
                            label={`${totals.completedCount}/${totals.totalCount}`}
                            color={totals.remainingCount === 0 ? 'success' : 'default'}
                            size="small"
                        />
                    </Stack>

                    {questGroups.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            No quests available yet.
                        </Typography>
                    )}

                    {questGroups.map(group => (
                        <Box key={group.id}>
                            <Stack spacing={1}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {group.title}
                                    </Typography>
                                    {group.isCompleted && (
                                        <Chip label={group.isSkipped ? 'Skipped' : 'Done'} size="small" color="success" />
                                    )}
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    {group.description}
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={group.progressPercent}
                                    sx={{ height: 6, borderRadius: 3 }}
                                />
                                <List dense>
                                    {group.quests.map(quest => (
                                        <ListItem key={quest.id} disableGutters>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Checkbox checked={quest.completed} disabled />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={quest.title}
                                                secondary={quest.description}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                                <Stack direction="row" spacing={1}>
                                    {!group.isCompleted && (
                                        <Button
                                            size="small"
                                            onClick={() => setGroupSkippedOrCompleted(group.id, true)}
                                        >
                                            Skip for now
                                        </Button>
                                    )}
                                    {group.isCompleted && group.isSkipped && (
                                        <Button
                                            size="small"
                                            onClick={() => setGroupSkippedOrCompleted(group.id, false)}
                                        >
                                            Show again
                                        </Button>
                                    )}
                                </Stack>
                                <Divider sx={{ my: 2 }} />
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </Box>
        </Drawer>
    );
};

export default ProfileQuestDrawer;
